/**
 * PUCK-11: Component tree — layer panel showing component hierarchy.
 * Click to select, drag to reorder, indent for nesting, eye icon for visibility.
 */

import { useCallback, useEffect, useMemo, useState, type ComponentType, type CSSProperties } from "react";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  ImageIcon,
  LayoutPanelTop,
  Lock,
  LockOpen,
  SquareStack,
  Type,
} from "lucide-react";
import type { LayerNode } from "@/components/pages/layer-data";

interface ComponentTreeProps {
  readonly nodes: readonly LayerNode[];
  readonly selectedId?: string;
  readonly onSelect: (id: string) => void;
  readonly onReorder: (dragId: string, dropId: string, position: "before" | "after" | "inside") => void;
  readonly onVisibilityToggle: (id: string) => void;
  readonly hiddenIds: ReadonlySet<string>;
  readonly onLockToggle: (id: string) => void;
  readonly lockedIds: ReadonlySet<string>;
  readonly remoteEditorsByComponent?: ReadonlyMap<string, readonly { userId: string; displayName: string; color: string }[]>;
}

const TYPE_ICON_MAP: Record<string, ComponentType<{ size?: number }>> = {
  image: ImageIcon,
  section: LayoutPanelTop,
  container: LayoutPanelTop,
  columns: LayoutPanelTop,
  text: Type,
  heading: Type,
};

const PANEL_STYLE: CSSProperties = {
  background: "var(--color-card)",
  border: "1px solid var(--color-border)",
  borderRadius: "8px",
  overflow: "auto",
  maxHeight: "400px",
};

const HEADER_STYLE: CSSProperties = {
  padding: "8px 12px",
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--color-muted-foreground)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: "1px solid var(--color-border)",
};

const NODE_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "6px 8px",
  cursor: "pointer",
  fontSize: "13px",
  color: "var(--color-foreground)",
  borderBottom: "1px solid var(--color-border)",
  transition: "background 0.1s",
};

const SELECTED_STYLE: CSSProperties = {
  ...NODE_STYLE,
  background: "var(--color-primary)",
  color: "var(--color-background)",
};

const ICON_BUTTON: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  border: "none",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  borderRadius: "4px",
  flexShrink: 0,
};

const TYPE_BADGE: CSSProperties = {
  fontSize: "10px",
  padding: "1px 4px",
  borderRadius: "3px",
  background: "var(--color-background)",
  color: "var(--color-muted-foreground)",
  marginLeft: "auto",
  flexShrink: 0,
};

const TYPE_ICON_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  color: "inherit",
  flexShrink: 0,
};

function collectExpandableIds(nodes: readonly LayerNode[]): string[] {
  return nodes.flatMap((node) => {
    const nested = collectExpandableIds(node.children ?? []);
    return node.children && node.children.length > 0 ? [node.id, ...nested] : nested;
  });
}

function getNodeIcon(type: string) {
  return TYPE_ICON_MAP[type.trim().toLowerCase()] ?? SquareStack;
}

function TreeNodeRow({
  node,
  depth,
  selectedId,
  hiddenIds,
  lockedIds,
  expandedIds,
  onSelect,
  onVisibilityToggle,
  onLockToggle,
  onToggleExpand,
  onDragStart,
  onDragOver,
  onDrop,
  remoteEditorsByComponent,
}: {
  readonly node: LayerNode;
  readonly depth: number;
  readonly selectedId?: string;
  readonly hiddenIds: ReadonlySet<string>;
  readonly lockedIds: ReadonlySet<string>;
  readonly expandedIds: ReadonlySet<string>;
  readonly onSelect: (id: string) => void;
  readonly onVisibilityToggle: (id: string) => void;
  readonly onLockToggle: (id: string) => void;
  readonly onToggleExpand: (id: string) => void;
  readonly onDragStart: (id: string) => void;
  readonly onDragOver: (event: React.DragEvent, id: string) => void;
  readonly onDrop: (event: React.DragEvent, id: string) => void;
  readonly remoteEditorsByComponent: ReadonlyMap<string, readonly { userId: string; displayName: string; color: string }[]>;
}) {
  const isSelected = selectedId === node.id;
  const isHidden = hiddenIds.has(node.id);
  const isLocked = lockedIds.has(node.id);
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const remoteEditors = remoteEditorsByComponent.get(node.id) ?? [];
  const hasRemoteEditors = remoteEditors.length > 0;
  const TypeIcon = getNodeIcon(node.type);

  return (
    <>
      <div
        style={{
          ...(isSelected ? SELECTED_STYLE : NODE_STYLE),
          paddingLeft: `${8 + depth * 16}px`,
          opacity: isHidden ? 0.4 : 1,
        }}
        onClick={() => onSelect(node.id)}
        draggable
        onDragStart={() => onDragStart(node.id)}
        onDragOver={(event) => onDragOver(event, node.id)}
        onDrop={(event) => onDrop(event, node.id)}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        <div style={ICON_BUTTON} aria-hidden>
          <GripVertical size={12} />
        </div>

        {hasChildren ? (
          <button
            type="button"
            style={ICON_BUTTON}
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpand(node.id);
            }}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <div style={{ width: "24px" }} />
        )}

        <div style={TYPE_ICON_STYLE} aria-hidden>
          <TypeIcon size={14} />
        </div>

        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {node.label}
        </span>

        <span style={TYPE_BADGE}>{node.type}</span>

        {hasRemoteEditors ? (
          <div
            aria-label={`Currently edited by ${remoteEditors.map((editor) => editor.displayName).join(", ")}`}
            title={`Currently edited by ${remoteEditors.map((editor) => editor.displayName).join(", ")}`}
            className="flex h-6 min-w-6 items-center justify-center rounded-md border border-amber-500/25 bg-amber-500/10 px-1.5 text-amber-700"
          >
            <Lock size={12} />
            {remoteEditors.length > 1 ? (
              <span className="ml-1 text-[10px] font-semibold leading-none">{remoteEditors.length}</span>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          style={ICON_BUTTON}
          onClick={(event) => {
            event.stopPropagation();
            onVisibilityToggle(node.id);
          }}
          aria-label={isHidden ? "Show component" : "Hide component"}
        >
          {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>

        <button
          type="button"
          style={ICON_BUTTON}
          onClick={(event) => {
            event.stopPropagation();
            onLockToggle(node.id);
          }}
          aria-label={isLocked ? "Unlock component" : "Lock component"}
        >
          {isLocked ? <Lock size={14} /> : <LockOpen size={14} />}
        </button>
      </div>

      {hasChildren && isExpanded
        ? node.children?.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              hiddenIds={hiddenIds}
              lockedIds={lockedIds}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onVisibilityToggle={onVisibilityToggle}
              onLockToggle={onLockToggle}
              onToggleExpand={onToggleExpand}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              remoteEditorsByComponent={remoteEditorsByComponent}
            />
          ))
        : null}
    </>
  );
}

export function ComponentTree({
  nodes,
  selectedId,
  onSelect,
  onReorder,
  onVisibilityToggle,
  hiddenIds,
  onLockToggle,
  lockedIds,
  remoteEditorsByComponent = new Map(),
}: ComponentTreeProps) {
  const expandableIds = useMemo(() => collectExpandableIds(nodes), [nodes]);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set(expandableIds));
  const [dragSourceId, setDragSourceId] = useState<string | null>(null);

  useEffect(() => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      for (const id of expandableIds) {
        next.add(id);
      }
      return next;
    });
  }, [expandableIds]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDragStart = useCallback((id: string) => {
    setDragSourceId(id);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent, _id: string) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent, targetId: string) => {
      event.preventDefault();
      if (!dragSourceId || dragSourceId === targetId) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top;
      const third = rect.height / 3;

      let position: "before" | "after" | "inside";
      if (y < third) {
        position = "before";
      } else if (y > third * 2) {
        position = "after";
      } else {
        position = "inside";
      }

      onReorder(dragSourceId, targetId, position);
      setDragSourceId(null);
    },
    [dragSourceId, onReorder],
  );

  return (
    <div style={PANEL_STYLE} role="tree" aria-label="Component layers">
      <div style={HEADER_STYLE}>Layers</div>
      {nodes.map((node) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          hiddenIds={hiddenIds}
          lockedIds={lockedIds}
          expandedIds={expandedIds}
          onSelect={onSelect}
          onVisibilityToggle={onVisibilityToggle}
          onLockToggle={onLockToggle}
          onToggleExpand={handleToggleExpand}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          remoteEditorsByComponent={remoteEditorsByComponent}
        />
      ))}
    </div>
  );
}
